const MAX_PAGOS = 20;

let secuenciaPago = 0;

const clavePago = () => `pago-${Date.now()}-${++secuenciaPago}`;

const textoMoneda = (centavos) => (centavos / 100).toFixed(2);

export const aCentavos = (valor) => {
  if (typeof valor === "number") {
    return Number.isFinite(valor) ? Math.round(valor * 100) : null;
  }
  const texto = String(valor ?? "").trim().replace(",", ".");
  if (!/^-?\d+(?:\.\d{1,2})?$/.test(texto)) return null;
  const numero = Number(texto);
  return Number.isFinite(numero) ? Math.round(numero * 100) : null;
};

export const normalizarFormaPago = (formaPago = {}) => ({
  ...formaPago,
  Id: Number(formaPago.Id ?? formaPago.formaPagoId ?? formaPago.FormaPagoId ?? formaPago.FormaPago_Id) || 0,
  sCodigo: String(formaPago.sCodigo ?? formaPago.codigo ?? formaPago.Codigo ?? "").trim(),
  sDescripcion: String(
    formaPago.sDescripcion ?? formaPago.descripcion ?? formaPago.Descripcion ?? formaPago.FormaPago ?? ""
  ).trim(),
  ForaneoId: Number(formaPago.ForaneoId) || 0,
  bDefault: Boolean(formaPago.bDefault),
  Seleccionado: Boolean(formaPago.Seleccionado),
  sExtra: String(formaPago.sExtra ?? ""),
  sExtra2: String(formaPago.sExtra2 ?? ""),
  iExtra: Number(formaPago.iExtra) || 0,
});

const normalizarClasificacion = (valor) => String(valor ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toUpperCase()
  .replace(/[^A-Z0-9]/g, "");

export const esCuentaCorriente = (formaPago = {}) => {
  const forma = normalizarFormaPago(formaPago);
  const codigo = normalizarClasificacion(forma.sCodigo);
  const descripcion = normalizarClasificacion(forma.sDescripcion);
  return ["CC", "CTACTE", "CUENTACORRIENTE"].includes(codigo)
    || descripcion.includes("CUENTACORRIENTE")
    || descripcion.includes("CTACTE");
};

export const filtrarFormasPagoConsumidorFinal = (lista = [], consumidorFinalGenerico = false) =>
  consumidorFinalGenerico ? lista.filter((formaPago) => !esCuentaCorriente(formaPago)) : lista;

export const obtenerFormaPagoInicial = (lista = []) =>
  lista.find((formaPago) => formaPago.bDefault) || null;

export const crearPago = ({ formaPago = null, importeCentavos = 0, personalizado = false } = {}) => ({
  ClaveUi: clavePago(),
  FormaPagoId: Number(formaPago?.Id) || 0,
  FormaPago: String(formaPago?.sDescripcion || ""),
  Importe: textoMoneda(Math.max(0, importeCentavos)),
  MontoRecibido: "",
  Vuelto: "0.00",
  Referencia: "",
  Personalizado: personalizado,
  ImportePersonalizado: personalizado,
});

export const crearPagoInicial = (total, catalogo = []) =>
  crearPago({
    formaPago: obtenerFormaPagoInicial(catalogo),
    importeCentavos: Math.max(0, aCentavos(total) || 0),
  });

export const totalizarPagos = (pagos = [], total = 0) => {
  const totalCentavos = Math.max(0, aCentavos(total) || 0);
  const asignadoCentavos = pagos.reduce((suma, pago) => {
    const importe = aCentavos(pago.Importe);
    return suma + (importe === null ? 0 : importe);
  }, 0);
  return {
    totalCentavos,
    asignadoCentavos,
    diferenciaCentavos: totalCentavos - asignadoCentavos,
    total: totalCentavos / 100,
    asignado: asignadoCentavos / 100,
    diferencia: (totalCentavos - asignadoCentavos) / 100,
  };
};

export const agregarPago = (pagos = [], total = 0, catalogo = []) => {
  if (pagos.length >= MAX_PAGOS) return pagos;
  const { diferenciaCentavos } = totalizarPagos(pagos, total);
  return [
    ...pagos,
    crearPago({
      formaPago: obtenerFormaPagoInicial(catalogo),
      importeCentavos: Math.max(0, diferenciaCentavos),
      personalizado: true,
    }),
  ];
};

export const actualizarPago = (pagos = [], clave, campo, valor, catalogo = []) =>
  pagos.map((pago) => {
    if (pago.ClaveUi !== clave) return pago;
    const actualizado = {
      ...pago,
      [campo]: valor,
      Personalizado: true,
      ImportePersonalizado: pago.ImportePersonalizado || campo === "Importe",
    };
    if (campo === "FormaPagoId") {
      const forma = catalogo.find((item) => item.Id === Number(valor));
      actualizado.FormaPagoId = forma?.Id || 0;
      actualizado.FormaPago = forma?.sDescripcion || "";
      if (forma && esCuentaCorriente(forma)) {
        actualizado.MontoRecibido = "";
        actualizado.Vuelto = "0.00";
      }
    }
    if (campo === "Importe" || campo === "MontoRecibido") {
      const importe = aCentavos(actualizado.Importe);
      const recibido = aCentavos(actualizado.MontoRecibido);
      actualizado.Vuelto = importe !== null && recibido !== null && recibido > 0
        ? textoMoneda(Math.max(0, recibido - importe))
        : "0.00";
    }
    return actualizado;
  });

export const eliminarPago = (pagos = [], clave) => pagos.filter((pago) => pago.ClaveUi !== clave);

export const sincronizarPagosConTotal = (pagos = [], total) => {
  if (pagos.length !== 1 || pagos[0].ImportePersonalizado) return pagos;
  const totalCentavos = Math.max(0, aCentavos(total) || 0);
  const pago = pagos[0];
  const recibido = aCentavos(pago.MontoRecibido);
  return [{
    ...pago,
    Importe: textoMoneda(totalCentavos),
    Vuelto: recibido !== null && recibido > 0
      ? textoMoneda(Math.max(0, recibido - totalCentavos))
      : "0.00",
  }];
};

export const validarPagos = (pagos = [], total = 0) => {
  const erroresPorClave = {};
  if (pagos.length === 0) erroresPorClave.general = ["Debe agregar al menos un pago."];
  if (pagos.length > MAX_PAGOS) erroresPorClave.general = [`Se admiten como maximo ${MAX_PAGOS} pagos.`];

  pagos.forEach((pago) => {
    const errores = [];
    const cuentaCorriente = esCuentaCorriente(pago);
    const importe = aCentavos(pago.Importe);
    const recibido = cuentaCorriente || pago.MontoRecibido === "" ? 0 : aCentavos(pago.MontoRecibido);
    const vuelto = cuentaCorriente || pago.Vuelto === "" ? 0 : aCentavos(pago.Vuelto);
    if (Number(pago.FormaPagoId) <= 0) errores.push("Seleccione una forma de pago.");
    if (importe === null) errores.push("El importe admite hasta dos decimales.");
    else if (importe <= 0) errores.push("El importe debe ser mayor que cero.");
    if (recibido === null) errores.push("El monto recibido admite hasta dos decimales.");
    else if (recibido < 0) errores.push("El monto recibido no puede ser negativo.");
    if (vuelto === null) errores.push("El vuelto admite hasta dos decimales.");
    else if (vuelto < 0) errores.push("El vuelto no puede ser negativo.");
    if (recibido === 0 && vuelto > 0) errores.push("No puede informar vuelto sin monto recibido.");
    if (recibido > 0 && importe !== null && vuelto !== null && Math.abs(recibido - vuelto - importe) > 1) {
      errores.push("El monto recibido menos el vuelto debe coincidir con el importe.");
    }
    if (String(pago.Referencia || "").length > 200) errores.push("La referencia admite hasta 200 caracteres.");
    if (errores.length) erroresPorClave[pago.ClaveUi] = errores;
  });

  const totales = totalizarPagos(pagos, total);
  if (totales.diferenciaCentavos !== 0) {
    erroresPorClave.total = [totales.diferenciaCentavos > 0
      ? "Queda saldo pendiente por asignar."
      : "El importe asignado supera el total de la venta."];
  }
  return { ...totales, erroresPorClave, valido: Object.keys(erroresPorClave).length === 0 };
};

export const serializarPagos = (pagos = []) => pagos.map((pago) => {
  const cuentaCorriente = esCuentaCorriente(pago);
  return {
    FormaPagoId: Number(pago.FormaPagoId) || 0,
    FormaPago: String(pago.FormaPago || ""),
    Importe: (aCentavos(pago.Importe) || 0) / 100,
    MontoRecibido: cuentaCorriente ? 0 : (aCentavos(pago.MontoRecibido) || 0) / 100,
    Vuelto: cuentaCorriente ? 0 : (aCentavos(pago.Vuelto) || 0) / 100,
    Referencia: String(pago.Referencia || ""),
  };
});

export const resumirPagos = (pagos = []) => {
  const lista = serializarPagos(pagos);
  return {
    FormaPago: lista.length === 1 ? lista[0].FormaPago : "Pago combinado",
    MontoRecibido: lista.reduce((suma, pago) => suma + pago.MontoRecibido, 0),
    Vuelto: lista.reduce((suma, pago) => suma + pago.Vuelto, 0),
    lstPagos: lista,
  };
};

export { MAX_PAGOS };
